/**
 * <ReplaceUsbImageDialog> — revoke a lost or leaked USB stick (JSS#890).
 *
 * A site's USB image carries its join credential: any box booted from it
 * joins the site. Replacing the image replaces that credential, so the old
 * USB stops working for new boxes. Boxes already installed keep working —
 * they reconnect with their own key. The ones that don't yet are secured
 * automatically first, and any box that can't be (offline, not installed)
 * is listed so the admin confirms it will need re-flashing.
 *
 *   1. Review: the dry-run preview, in plain words, grouped by what happens,
 *      and the boxes that joined with the current image (JSS#883 security
 *      review) — if it leaked, one nobody recognises may be someone else's.
 *   2. Confirm, with an explicit acknowledgement when a box will need
 *      re-flashing. A 409 `boxes_would_be_stranded` (a box that could not be
 *      secured just now, or one that booted since the preview) shows the new
 *      list and asks again.
 *   3. Done: download the new USB image.
 *
 * Admin only: the BFF and deployment-service enforce it; the RoleGate around
 * the button is the UX layer.
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download, Loader2, ShieldCheck, X } from 'lucide-react';
import { sitesApi } from '@/api/client';
import { parseApiError } from '@/lib/apiError';
import type {
  BootMediaAtRiskMachine,
  BootMediaJoinedWithCurrentImage,
  BootMediaRiskCategory,
  BootMediaRotationResult,
  BootMediaRotationSummary,
  BootMediaStrandedResponse,
} from '@/types';

type Arch = 'arm64' | 'amd64';

interface Props {
  siteId: string;
  siteName: string;
  onClose: () => void;
  /** Download the site's (new) USB image — the Boot Images card's own download. */
  onDownload: (arch: Arch) => void;
  /** The arch being downloaded right now, if any. */
  downloading: Arch | null;
  /** The image was replaced: refresh whatever shows the site. */
  onReplaced?: () => void;
}

const GROUPS: Array<{ category: BootMediaRiskCategory; title: string }> = [
  { category: 'will_secure', title: 'Secured automatically before the image is replaced' },
  { category: 'seal_failed', title: 'Could not be secured just now' },
  { category: 'offline', title: 'Offline' },
  { category: 'not_installed', title: 'Not installed yet' },
];

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function isStranded(body: unknown): body is BootMediaStrandedResponse {
  return !!body && typeof body === 'object' && (body as { error?: unknown }).error === 'boxes_would_be_stranded';
}

function AtRiskList({ atRisk }: { atRisk: BootMediaAtRiskMachine[] }) {
  return (
    <div className="space-y-3" data-testid="replace-usb-at-risk">
      {GROUPS.map(({ category, title }) => {
        const boxes = atRisk.filter((m) => m.category === category);
        if (boxes.length === 0) return null;
        const safe = category === 'will_secure';
        return (
          <div
            key={category}
            data-testid={`replace-usb-group-${category}`}
            className={
              safe
                ? 'rounded-lg border border-surface-elevated bg-surface-deep p-3'
                : 'rounded-lg border border-amber-400/40 bg-amber-400/10 p-3'
            }
          >
            <div className={`text-xs font-medium mb-1.5 ${safe ? 'text-silver-bright' : 'text-amber-300'}`}>{title}</div>
            <ul className="space-y-1">
              {boxes.map((m) => (
                <li key={m.id} className="text-xs" title={m.detail}>
                  <span className="font-mono text-silver-bright">{m.hostname || m.id}</span>
                  <span className="text-silver-muted"> — {m.message}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function JoinedList({ joined }: { joined: BootMediaJoinedWithCurrentImage }) {
  const since = joined.sinceReplacement ? new Date(joined.since).toLocaleDateString() : 'the site was created';
  return (
    <div className="rounded-lg border border-surface-elevated bg-surface-deep p-3" data-testid="replace-usb-joined">
      <div className="text-xs font-medium text-silver-bright mb-1.5">
        Boxes that joined with this USB image since {since}
      </div>
      <ul className="space-y-1">
        {joined.machines.map((m) => (
          <li key={m.id} className="text-xs">
            <span className="font-mono text-silver-bright">{m.hostname || m.id}</span>
            <span className="text-silver-muted"> — joined {new Date(m.enrolledAt).toLocaleString()}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-silver-muted mt-2">If you don&apos;t recognise a box here, remove it.</p>
    </div>
  );
}

function DoneBody({ result, onDownload, downloading }: { result: BootMediaRotationResult } & Pick<Props, 'onDownload' | 'downloading'>) {
  return (
    <div className="space-y-3" data-testid="replace-usb-done">
      <div className="flex items-start gap-2 rounded-lg border border-status-healthy/40 bg-status-healthy/10 p-3 text-sm text-status-healthy">
        <ShieldCheck size={16} className="mt-0.5 shrink-0" />
        <div>
          <div className="font-medium">USB image replaced</div>
          <div className="text-xs text-silver-muted mt-1">
            The old USB no longer adds boxes to this site. Boxes already installed keep working.
            {result.securedNow.length > 0 && (
              <> Secured first: {result.securedNow.map((m) => m.hostname).join(', ')}.</>
            )}
          </div>
        </div>
      </div>
      {result.stranded.length > 0 && (
        <div className="rounded-lg border border-amber-400/40 bg-amber-400/10 p-3 text-xs" data-testid="replace-usb-reflash">
          <div className="font-medium text-amber-300 mb-1">Re-flash with the new USB image</div>
          <div className="text-silver-muted">
            {result.stranded.map((m) => m.hostname || m.id).join(', ')}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="replace-usb-download-arm64"
          onClick={() => onDownload('arm64')}
          disabled={downloading !== null}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gold text-surface-deep hover:bg-gold-light disabled:opacity-50 transition-colors"
        >
          {downloading === 'arm64' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Download the new USB image
        </button>
        <button
          type="button"
          onClick={() => onDownload('amd64')}
          disabled={downloading !== null}
          className="text-xs text-silver-muted hover:text-silver-bright disabled:opacity-50 transition-colors"
        >
          {downloading === 'amd64' ? 'Downloading AMD64…' : 'AMD64 image'}
        </button>
      </div>
      <p className="text-[10px] text-silver-muted">
        ARM64 is for DGX Spark.{!result.bootImageReady && ' The first download can take a minute or two while the image is built.'}{' '}
        Delete old copies of the image; they no longer work.
      </p>
    </div>
  );
}

export function ReplaceUsbImageDialog({ siteId, siteName, onClose, onDownload, downloading, onReplaced }: Props) {
  const preview = useQuery({
    queryKey: ['boot-media-preview', siteId],
    queryFn: () => sitesApi.previewBootMediaRotation(siteId),
    refetchOnWindowFocus: false,
  });
  // After a 409, the server's newer list replaces the preview.
  const [refused, setRefused] = useState<{ atRisk: BootMediaAtRiskMachine[]; summary: BootMediaRotationSummary } | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const atRisk = refused?.atRisk ?? preview.data?.atRisk ?? [];
  const summary = refused?.summary ?? preview.data?.summary;
  // Optional: an older deployment-service doesn't send it.
  const joined = preview.data?.joinedWithCurrentImage;
  // Boxes that will need re-flashing: everything JSS can't secure itself.
  const needsReflash = atRisk.filter((m) => m.category !== 'will_secure');

  const rotate = useMutation({
    mutationFn: () => sitesApi.rotateBootMedia(siteId, needsReflash.map((m) => m.id)),
    onMutate: () => setError(null),
    onSuccess: () => onReplaced?.(),
    onError: (err) => {
      const parsed = parseApiError(err);
      if (parsed.status === 409 && isStranded(parsed.body)) {
        setRefused({ atRisk: parsed.body.atRisk, summary: parsed.body.summary });
        setAcknowledged(false);
        return;
      }
      setError(parsed.message);
    },
  });

  const result = rotate.data;
  const canConfirm = !!summary && !rotate.isPending && (needsReflash.length === 0 || acknowledged);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Replace USB image"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-surface-elevated rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="replace-usb-dialog"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h3 className="text-silver-bright font-semibold">Replace USB image — {siteName}</h3>
          <button type="button" onClick={onClose} className="text-silver-muted hover:text-silver-bright transition-colors" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {result ? (
            <DoneBody result={result} onDownload={onDownload} downloading={downloading} />
          ) : (
            <>
              <p className="text-sm text-silver-muted">
                Use this if a USB stick for this site is lost, or a copy of its image went somewhere it shouldn&apos;t.
                The old USB stops working for new boxes. Boxes already installed keep working.
              </p>

              {preview.isLoading && <p className="text-sm text-silver-muted">Checking this site&apos;s boxes…</p>}
              {preview.error && (
                <div className="rounded-lg border border-status-error/40 bg-status-error/10 p-3 text-sm text-status-error">
                  {parseApiError(preview.error).message}
                </div>
              )}

              {summary && (
                <>
                  {summary.secured > 0 && (
                    <div className="flex items-center gap-2 text-xs text-silver-muted" data-testid="replace-usb-secured">
                      <ShieldCheck size={14} className="text-status-healthy shrink-0" />
                      {plural(summary.secured, 'box is', 'boxes are')} already secured and won&apos;t be affected.
                    </div>
                  )}
                  {atRisk.length === 0 ? (
                    <p className="text-xs text-silver-muted" data-testid="replace-usb-nothing-at-risk">
                      No box depends on the current USB image.
                    </p>
                  ) : (
                    <AtRiskList atRisk={atRisk} />
                  )}
                  {joined && joined.machines.length > 0 && <JoinedList joined={joined} />}
                </>
              )}

              {refused && (
                <div className="flex items-start gap-2 text-xs text-amber-300" data-testid="replace-usb-refused">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  Not replaced yet: some boxes still depend on the current USB image. Review the list and confirm again.
                </div>
              )}

              {needsReflash.length > 0 && (
                <label className="flex items-start gap-2 text-xs text-silver-bright">
                  <input
                    type="checkbox"
                    data-testid="replace-usb-ack"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    I understand: {needsReflash.length === 1 ? 'this box' : `these ${needsReflash.length} boxes`} will need
                    re-flashing with the new USB image.
                  </span>
                </label>
              )}

              {rotate.isPending && (
                <p className="text-xs text-silver-muted flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" />
                  Securing installed boxes, then replacing the image. This can take up to a minute.
                </p>
              )}
              {error && (
                <div className="rounded-lg border border-status-error/40 bg-status-error/10 p-3 text-sm text-status-error" data-testid="replace-usb-error">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          {result ? (
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors">
              Close
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors">
                Cancel
              </button>
              <button
                type="button"
                data-testid="replace-usb-confirm"
                onClick={() => rotate.mutate()}
                disabled={!canConfirm}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-status-error text-white hover:bg-status-error/80 disabled:bg-surface-elevated disabled:text-silver-muted disabled:cursor-not-allowed transition-colors"
              >
                {rotate.isPending ? 'Replacing…' : 'Replace USB image'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReplaceUsbImageDialog;
